import React, { useState } from 'react';

import {
  MEMORY_KINDS,
  MemoryEdits,
  MemoryFormValues,
  MemoryKind,
  MemoryScope,
} from './memoryApi';

interface Props {
  initial?: Partial<MemoryEdits> & { scope?: MemoryScope };
  /** Offered only when adding. More than one draws a picker. */
  scopes?: { value: MemoryScope; label: string }[];
  submitLabel: string;
  busy: boolean;
  onSubmit: (values: MemoryFormValues) => void;
  onCancel: () => void;
}

/** One decision's fields, for adding one, editing one, or editing a proposal first. */
export function MemoryForm({
  initial,
  scopes,
  submitLabel,
  busy,
  onSubmit,
  onCancel,
}: Props) {
  const [values, setValues] = useState<MemoryFormValues>({
    scope: initial?.scope ?? scopes?.[0]?.value ?? 'model',
    kind: (initial?.kind as MemoryKind) ?? 'requirement',
    text: initial?.text ?? '',
    instead_of: initial?.instead_of ?? '',
    reason: initial?.reason ?? '',
  });
  const set = <K extends keyof MemoryFormValues>(
    key: K,
    value: MemoryFormValues[K]
  ) => setValues((was) => ({ ...was, [key]: value }));

  return (
    <form
      className="mem-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (values.text.trim()) onSubmit(values);
      }}
    >
      <div className="mem-form-row">
        {scopes && scopes.length > 1 && (
          <select
            className="mem-select"
            aria-label="Applies to"
            value={values.scope}
            disabled={busy}
            onChange={(e) => set('scope', e.target.value as MemoryScope)}
          >
            {scopes.map((scope) => (
              <option key={scope.value} value={scope.value}>
                {scope.label}
              </option>
            ))}
          </select>
        )}
        <select
          className="mem-select"
          aria-label="Kind"
          value={values.kind}
          disabled={busy}
          onChange={(e) => set('kind', e.target.value as MemoryKind)}
        >
          {MEMORY_KINDS.map((kind) => (
            <option key={kind.value} value={kind.value}>
              {kind.label}
            </option>
          ))}
        </select>
      </div>
      <textarea
        className="mem-input"
        aria-label="Decision"
        placeholder="Decision"
        rows={2}
        autoFocus
        value={values.text}
        disabled={busy}
        onChange={(e) => set('text', e.target.value)}
      />
      <input
        className="mem-input"
        aria-label="Instead of"
        placeholder="Instead of"
        value={values.instead_of}
        disabled={busy}
        onChange={(e) => set('instead_of', e.target.value)}
      />
      <input
        className="mem-input"
        aria-label="Why"
        placeholder="Why"
        value={values.reason}
        disabled={busy}
        onChange={(e) => set('reason', e.target.value)}
      />
      <div className="mem-actions">
        <button
          type="submit"
          className="mem-btn mem-btn--primary"
          disabled={busy || !values.text.trim()}
        >
          {busy ? 'Saving…' : submitLabel}
        </button>
        <button
          type="button"
          className="mem-btn"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
